import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row Types ───

export interface PatientFileRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  kind: string | null;
  filename: string | null;
  url: string | null;
  tag: string | null;
  mime_type: string | null;
  description: string | null;
  starred: number | null; // 0 or 1
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientNoteRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  visit_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  title: string | null;
  body: string | null;
  note_type: string | null; // 'general' | 'clinical' | 'follow_up' | 'admin'
  visibility: string | null; // 'team' | 'private' | 'front_desk'
  alert_level: string | null; // 'none' | 'info' | 'warning' | 'critical'
  author_user_id: string | null;
  author_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface PatientFormRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  appointment_id: string | null;
  queue_id: string | null;
  name: string | null;
  tag: string | null;
  created_at: string | null;
  updated_at: string | null;
}

// ─── Queries ───

export function qPatientFiles(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_files', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_files WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['patient_files'],
  };
}

export function qPatientFilesByEncounter(
  patientId: string,
  encounterId: string,
): QuerySpec<[string, string]> {
  return {
    key: ['patient_files', 'by-encounter', patientId, encounterId] as const,
    sql: `SELECT * FROM patient_files WHERE patient_id = ? AND encounter_id = ? ORDER BY created_at DESC`,
    params: [patientId, encounterId],
    watch: ['patient_files'],
  };
}

export function qPatientNotes(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_notes WHERE patient_id = ? ORDER BY updated_at DESC`,
    params: [patientId],
    watch: ['patient_notes'],
  };
}

export function qNotesByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_notes WHERE encounter_id = ? ORDER BY created_at DESC`,
    params: [encounterId],
    watch: ['patient_notes'],
  };
}

export function qPatientAlertNotes(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'alerts', patientId] as const,
    sql: `SELECT * FROM patient_notes
          WHERE patient_id = ?
            AND alert_level IN ('info', 'warning', 'critical')
            AND visibility IN ('team', 'front_desk')
          ORDER BY created_at DESC
          LIMIT 3`,
    params: [patientId],
    watch: ['patient_notes'],
  };
}

export function qNotesPreviewByEncounter(encounterId: string): QuerySpec<[string]> {
  return {
    key: ['patient_notes', 'preview-by-encounter', encounterId] as const,
    sql: `SELECT * FROM patient_notes WHERE encounter_id = ? ORDER BY created_at DESC LIMIT 3`,
    params: [encounterId],
    watch: ['patient_notes'],
  };
}

export function qPatientForms(patientId: string): QuerySpec<[string]> {
  return {
    key: ['patient_forms', 'by-patient', patientId] as const,
    sql: `SELECT * FROM patient_forms WHERE patient_id = ? ORDER BY created_at DESC`,
    params: [patientId],
    watch: ['patient_forms'],
  };
}
